import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Cell } from 'recharts';

interface VersionStat {
  version: string;
  activeSites: number;
  connectedSites: number;
  offlineSites: number;
}

interface VersionBarChartProps {
  data: VersionStat[];
  onVersionClick?: (version: string) => void;
  latestVersion?: string;
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    const activeSites = payload.find((p: any) => p.dataKey === 'activeSites')?.value || 0;
    const connectedSites = payload.find((p: any) => p.dataKey === 'connectedSites')?.value || 0;
    const totalSites = activeSites + connectedSites;
    
    return (
      <div className="bg-card border border-border rounded-lg p-3 shadow-lg">
        <p className="font-medium text-card-foreground">Version {label}</p>
        <p className="text-sm text-muted-foreground">
          Total Sites: <span className="font-medium">{totalSites}</span>
        </p>
        <p className="text-sm text-muted-foreground">
          Active: <span className="font-medium text-blue-600">{activeSites}</span>
        </p>
        <p className="text-sm text-muted-foreground">
          Connected: <span className="font-medium text-red-400">{connectedSites}</span>
        </p>
      </div>
    );
  }
  return null;
};

export const VersionBarChart = ({ data, onVersionClick, latestVersion }: VersionBarChartProps) => {
  return (
    <div className="w-full h-80">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
          <XAxis 
            dataKey="version" 
            tick={(props) => {
              const isLatestVersion = latestVersion && props.payload.value === latestVersion;
              return (
                <text
                  x={props.x}
                  y={props.y}
                  dy={16}
                  textAnchor={props.textAnchor}
                  fill={isLatestVersion ? 'hsl(var(--primary))' : 'hsl(var(--foreground))'}
                  fontSize={12}
                  fontWeight={isLatestVersion ? 'bold' : 'normal'}
                >
                  {props.payload.value}
                </text>
              );
            }}
            axisLine={{ stroke: 'hsl(var(--border))' }}
          />
          <YAxis 
            tick={{ fontSize: 12, fill: 'hsl(var(--foreground))' }}
            axisLine={{ stroke: 'hsl(var(--border))' }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend 
            wrapperStyle={{ 
              color: 'hsl(var(--foreground))'
            }}
          />
          <Bar 
            dataKey="activeSites" 
            name="Active Sites"
            fill="hsl(210, 100%, 50%)"
            radius={[4, 4, 0, 0]}
            className="hover:opacity-80 transition-opacity duration-200"
            style={{ cursor: onVersionClick ? 'pointer' : 'default' }}
            onClick={(data) => onVersionClick && onVersionClick(data.version)}
          >
            {data.map((entry, index) => {
              const isLatestVersion = latestVersion && entry.version === latestVersion;
              return (
                <Cell 
                  key={`cell-active-${index}`} 
                  fill="hsl(210, 100%, 50%)"
                  style={{
                    filter: isLatestVersion ? 'drop-shadow(0 6px 12px rgba(0, 0, 0, 0.5))' : 'none'
                  }}
                />
              );
            })}
          </Bar>
          <Bar 
            dataKey="connectedSites" 
            name="Connected Sites"
            fill="hsl(0, 70%, 80%)"
            radius={[4, 4, 0, 0]}
            className="hover:opacity-80 transition-opacity duration-200"
            style={{ cursor: onVersionClick ? 'pointer' : 'default' }}
            onClick={(data) => onVersionClick && onVersionClick(data.version)}
          >
            {data.map((entry, index) => {
              const isLatestVersion = latestVersion && entry.version === latestVersion;
              return (
                <Cell 
                  key={`cell-connected-${index}`} 
                  fill="hsl(0, 70%, 80%)"
                  style={{
                    filter: isLatestVersion ? 'drop-shadow(0 6px 12px rgba(0, 0, 0, 0.5))' : 'none'
                  }}
                />
              );
            })}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};